<template>
  <div class="main-page">
    <!-- 메인 배너 섹션 -->
    <section class="hero-banner" :style="{ background: activeIndex === 0 ? '#1E90FF' : (activeIndex === 1 ? '#03C75A' : '#f37d2a') }">
      <div class="banner-wrapper">
        <div class="banner-container">
          <!-- 모바일 네비게이션 (우측 상단) -->
          <div class="mobile-nav-control">
            <button class="mobile-nav-btn prev" @click="goPrev">
              <i class="fa fa-chevron-left"></i>
            </button>
            <button class="mobile-nav-btn next" @click="goNext">
              <i class="fa fa-chevron-right"></i>
            </button>
            <div class="slide-counter">{{ activeIndex + 1 }}/3</div>
          </div>

          <!-- Swiper 컴포넌트 -->
          <div class="swiper banner-swiper" ref="bannerSwiper">
            <div class="swiper-wrapper">
              <div class="swiper-slide">
                <div class="banner-slide banner-slide-1"></div>
              </div>
              <div class="swiper-slide">
                <div class="banner-slide banner-slide-2"></div>
              </div>
              <div class="swiper-slide">
                <div class="banner-slide banner-slide-3"></div>
              </div>
            </div>
            <!-- 페이지네이션 -->
            <div class="swiper-pagination"></div>
            <!-- 네비게이션 버튼 (데스크톱) -->
            <div class="swiper-button-prev"></div>
            <div class="swiper-button-next"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- 실시간 수집 상태 바 -->
    <section class="collection-status-bar">
      <div class="container">
        <div class="status-bar-content">
          <div class="status-indicator" :class="{ active: isCollecting }">
            <div class="pulse-dot"></div>
            <span class="status-text">{{ isCollecting ? 'DB 수집 중' : '대기 중' }}</span>
          </div>
          <div class="collection-info">
            <div class="info-item">
              <i class="fa fa-refresh"></i>
              <span>실시간 업데이트</span>
            </div>
            <div class="info-item">
              <i class="fa fa-clock-o"></i>
              <span>마지막 업데이트: {{ lastUpdateTime }}</span>
            </div>
            <div class="info-item">
              <i class="fa fa-calendar-check-o"></i>
              <span>오늘 수집: <strong>{{ todayCollected.toLocaleString() }}건</strong></span>
            </div>
          </div>
          <div class="progress-bar-wrapper">
            <div class="progress-bar-bg">
              <div class="progress-bar-fill" :style="{ width: progressWidth + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 통계 섹션 -->
    <section class="stats-section">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="fa fa-calendar-check-o"></i>
            </div>
            <div class="stat-content">
              <label>오늘 등록</label>
              <h3>{{ todayTotalCount.toLocaleString() }}<span>건</span></h3>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">
              <i class="fa fa-calendar"></i>
            </div>
            <div class="stat-content">
              <label>어제 등록</label>
              <h3>{{ ((businessItemTotalCount.yesterday || 0) + (openMarketItemTotalCount.yesterday || 0) + (corpTotalCount.yesterday || 0) + (officeTotalCount.yesterday || 0) + (licenseTotalCount.yesterday || 0)).toLocaleString() }}<span>건</span></h3>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">
              <i class="fa fa-calendar-o"></i>
            </div>
            <div class="stat-content">
              <label>이번달 등록</label>
              <h3>{{ ((businessItemTotalCount.monthday || 0) + (openMarketItemTotalCount.monthday || 0) + (corpTotalCount.monthday || 0) + (officeTotalCount.monthday || 0) + (licenseTotalCount.monthday || 0)).toLocaleString() }}<span>건</span></h3>
            </div>
          </div>
          <div class="stat-card total-card">
            <div class="stat-badge total-badge">TOTAL</div>
            <div class="stat-icon">
              <i class="fa fa-database"></i>
            </div>
            <div class="stat-content">
              <label>전체 DB</label>
              <h3>
                <ICountUp
                  :delay="delay"
                  :endVal="totalDBCount"
                  :options="options"
                  @ready="onReady"
                />
                <span>건</span>
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 인기 업종 카테고리 -->
    <section class="category-section">
      <div class="container">
        <div class="section-header">
          <h2>업종별 통합 검색</h2>
          <p>원하시는 업종의 통합 DB를 검색하세요</p>
        </div>
        <div class="category-grid">
          <div v-for="category in directDepthCategories" :key="category"
               class="category-item"
               @click="goToCategorySearch(category)">
            <div class="category-icon">
              <i :class="getCategoryIcon(category)"></i>
            </div>
            <span>{{ category }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 오픈마켓 DB 섹션 -->
    <section class="db-section">
      <div class="container">
        <div class="db-header">
          <div class="db-title">
            <div class="db-icon">
              <i class="fa fa-shopping-bag"></i>
            </div>
            <div>
              <h3>
                오픈마켓 DB
                <span class="collecting-indicator">
                  <i class="fa fa-circle collecting-dot"></i>
                  <span class="collecting-text">실시간 수집중</span>
                </span>
              </h3>
              <p>오픈마켓 셀러 정보를 확인하세요</p>
            </div>
          </div>
          <div class="db-stats">
            <div class="db-stat">
              <span class="label">총 업체수</span>
              <span class="value">{{ openMarketItemTotalCount.total.toLocaleString() }}</span>
            </div>
            <div class="db-stat">
              <span class="label">오늘 업데이트</span>
              <span class="value">{{ openMarketItemTotalCount.today.toLocaleString() }}건</span>
            </div>
          </div>
        </div>

        <div v-if="isOpenMarketLoding" class="loading-wrapper">
          <b-skeleton-table
            :rows="10"
            :columns="6"
            :table-props="{ bordered: false, striped: false }"
          ></b-skeleton-table>
        </div>
        <div v-else class="db-table-wrapper">
          <div class="mobile-scroll-notice">
            모바일 화면에서는 좌우로 스크롤 해주세요.
          </div>
          <table class="db-table">
            <thead>
              <tr>
                <th>업체명</th>
                <th>카테고리</th>
                <th>전화번호</th>
                <th>분류</th>
                <th>업데이트</th>
                <th>상세</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in openMarketItemModel" :key="index">
                <td class="company-name">
                  <span @click="copyText(item.title)">
                    {{ item.title }}
                    <img v-if="isNewItem(item.createDate)" src="/assets/images/new_icon.png" alt="new" class="new-icon">
                  </span>
                </td>
                <td>{{ item.depth }}</td>
                <td class="phone">
                  <i v-if="item.phone" class="fa fa-phone"></i>
                  {{ item.phone || '-' }}
                </td>
                <td>
                  <span :class="getMarketClass(item.market)">{{ item.market }}</span>
                </td>
                <td>{{ $dayjs(item.createDate).format('YYYY-MM-DD') }}</td>
                <td class="action">
                  <button @click="GetLink('오픈마켓', item.link)" class="btn-detail">
                    <i class="fa fa-external-link"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- 사업자 DB 섹션 -->
    <section class="db-section bg-light">
      <div class="container">
        <div class="db-header">
          <div class="db-title">
            <div class="db-icon">
              <i class="fa fa-building"></i>
            </div>
            <div>
              <h3>
                사업자 DB
                <span class="collecting-indicator">
                  <i class="fa fa-circle collecting-dot"></i>
                  <span class="collecting-text">실시간 수집중</span>
                </span>
              </h3>
              <p>업종별 사업자 정보를 확인하세요</p>
            </div>
          </div>
          <div class="db-stats">
            <div class="db-stat">
              <span class="label">총 업체수</span>
              <span class="value">{{ businessItemTotalCount.total.toLocaleString() }}</span>
            </div>
            <div class="db-stat">
              <span class="label">오늘 업데이트</span>
              <span class="value">{{ businessItemTotalCount.today.toLocaleString() }}건</span>
            </div>
          </div>
        </div>

        <div v-if="isBusinessLoding" class="loading-wrapper">
          <b-skeleton-table
            :rows="10"
            :columns="6"
            :table-props="{ bordered: false, striped: false }"
          ></b-skeleton-table>
        </div>
        <div v-else class="db-table-wrapper">
          <div class="mobile-scroll-notice">
            모바일 화면에서는 좌우로 스크롤 해주세요.
          </div>
          <table class="db-table">
            <thead>
              <tr>
                <th>업체명</th>
                <th>주소</th>
                <th>분류</th>
                <th>전화번호</th>
                <th>업데이트</th>
                <th>상세</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in businessItemModel" :key="index">
                <td class="company-name">
                  <span @click="copyText(item.title)">
                    {{ item.title }}
                    <img v-if="isNewItem(item.createDate)" src="/assets/images/new_icon.png" alt="new" class="new-icon">
                  </span>
                </td>
                <td class="address">{{ item.address }}</td>
                <td>{{ item.depth }}</td>
                <td class="phone">
                  <i v-if="item.phone" class="fa fa-phone"></i>
                  {{ item.phone || '-' }}
                </td>
                <td>{{ $dayjs(item.createDate).format('YYYY-MM-DD') }}</td>
                <td class="action">
                  <button @click="GetLink('사업자', item.link)" class="btn-detail">
                    <i class="fa fa-external-link"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- 기업 DB 섹션 -->
    <section class="db-section">
      <div class="container">
        <div class="db-header">
          <div class="db-title">
            <div class="db-icon">
              <i class="fa fa-briefcase"></i>
            </div>
            <div>
              <h3>
                기업 DB
                <span class="collecting-indicator">
                  <i class="fa fa-circle collecting-dot"></i>
                  <span class="collecting-text">실시간 수집중</span>
                </span>
              </h3>
              <p>기업 정보를 확인하세요</p>
            </div>
          </div>
          <div class="db-stats">
            <div class="db-stat">
              <span class="label">총 기업수</span>
              <span class="value">{{ officeTotalCount.total.toLocaleString() }}</span>
            </div>
            <div class="db-stat">
              <span class="label">오늘 업데이트</span>
              <span class="value">{{ officeTotalCount.today.toLocaleString() }}건</span>
            </div>
          </div>
        </div>

        <div v-if="isOfficeLoding" class="loading-wrapper">
          <b-skeleton-table
            :rows="10"
            :columns="6"
            :table-props="{ bordered: false, striped: false }"
          ></b-skeleton-table>
        </div>
        <div v-else class="db-table-wrapper">
          <div class="mobile-scroll-notice">
            모바일 화면에서는 좌우로 스크롤 해주세요.
          </div>
          <table class="db-table">
            <thead>
              <tr>
                <th>상호명</th>
                <th>업종</th>
                <th>전화번호</th>
                <th>대표자</th>
                <th>주소</th>
                <th>업데이트</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in officeItemModel" :key="index">
                <td class="company-name">
                  <span @click="copyText(item.title)">
                    {{ item.title }}
                    <img v-if="isNewItem(item.createDate)" src="/assets/images/new_icon.png" alt="new" class="new-icon">
                  </span>
                </td>
                <td>{{ item.depth }}</td>
                <td class="phone">
                  <i v-if="item.phone" class="fa fa-phone"></i>
                  {{ item.phone || '-' }}
                </td>
                <td>{{ item.name || '-' }}</td>
                <td class="address">{{ item.address }}</td>
                <td>{{ $dayjs(item.createDate).format('YYYY-MM-DD') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- 법인 DB 섹션 -->
    <section class="db-section bg-light">
      <div class="container">
        <div class="db-header">
          <div class="db-title">
            <div class="db-icon">
              <i class="fa fa-university"></i>
            </div>
            <div>
              <h3>
                법인 DB
                <span class="collecting-indicator">
                  <i class="fa fa-circle collecting-dot"></i>
                  <span class="collecting-text">실시간 수집중</span>
                </span>
              </h3>
              <p>법인 정보를 확인하세요</p>
            </div>
          </div>
          <div class="db-stats">
            <div class="db-stat">
              <span class="label">총 법인수</span>
              <span class="value">{{ corpTotalCount.total.toLocaleString() }}</span>
            </div>
            <div class="db-stat">
              <span class="label">오늘 업데이트</span>
              <span class="value">{{ corpTotalCount.today.toLocaleString() }}건</span>
            </div>
          </div>
        </div>

        <div v-if="isCorpLoding" class="loading-wrapper">
          <b-skeleton-table
            :rows="10"
            :columns="6"
            :table-props="{ bordered: false, striped: false }"
          ></b-skeleton-table>
        </div>
        <div v-else class="db-table-wrapper">
          <div class="mobile-scroll-notice">
            모바일 화면에서는 좌우로 스크롤 해주세요.
          </div>
          <table class="db-table">
            <thead>
              <tr>
                <th>업체명</th>
                <th>분류</th>
                <th>전화번호</th>
                <th>대표자</th>
                <th>주소</th>
                <th>업데이트</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in corpItemModel" :key="index">
                <td class="company-name">
                  <span @click="copyText(item.title)">
                    {{ item.title }}
                    <img v-if="isNewItem(item.createDate)" src="/assets/images/new_icon.png" alt="new" class="new-icon">
                  </span>
                </td>
                <td>{{ item.depth }}</td>
                <td class="phone">
                  <i v-if="item.phone" class="fa fa-phone"></i>
                  {{ item.phone || '-' }}
                </td>
                <td>{{ item.name || '-' }}</td>
                <td class="address">{{ item.address }}</td>
                <td>{{ $dayjs(item.createDate).format('YYYY-MM-DD') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- 인허가 DB 섹션 -->
    <section class="db-section">
      <div class="container">
        <div class="db-header">
          <div class="db-title">
            <div class="db-icon">
              <i class="fa fa-certificate"></i>
            </div>
            <div>
              <h3>
                인허가 DB
                <span class="collecting-indicator">
                  <i class="fa fa-circle collecting-dot"></i>
                  <span class="collecting-text">실시간 수집중</span>
                </span>
              </h3>
              <p>인허가 정보를 확인하세요</p>
            </div>
          </div>
          <div class="db-stats">
            <div class="db-stat">
              <span class="label">총 인허가수</span>
              <span class="value">{{ licenseTotalCount.total.toLocaleString() }}</span>
            </div>
            <div class="db-stat">
              <span class="label">오늘 업데이트</span>
              <span class="value">{{ licenseTotalCount.today.toLocaleString() }}건</span>
            </div>
          </div>
        </div>

        <div v-if="isLicenseLoding" class="loading-wrapper">
          <b-skeleton-table
            :rows="10"
            :columns="5"
            :table-props="{ bordered: false, striped: false }"
          ></b-skeleton-table>
        </div>
        <div v-else class="db-table-wrapper">
          <div class="mobile-scroll-notice">
            모바일 화면에서는 좌우로 스크롤 해주세요.
          </div>
          <table class="db-table">
            <thead>
              <tr>
                <th>업체명</th>
                <th>분류</th>
                <th>전화번호</th>
                <th>주소</th>
                <th>업데이트</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in licenseItemModel" :key="index">
                <td class="company-name">
                  <span @click="copyText(item.title)">
                    {{ item.title }}
                    <img v-if="isNewItem(item.createDate)" src="/assets/images/new_icon.png" alt="new" class="new-icon">
                  </span>
                </td>
                <td>{{ item.depth }}</td>
                <td class="phone">
                  <i v-if="item.phone" class="fa fa-phone"></i>
                  {{ item.phone || '-' }}
                </td>
                <td class="address">{{ item.address }}</td>
                <td>{{ $dayjs(item.createDate).format('YYYY-MM-DD') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { oepnMarketAPI, businessAPI, corpAPI, officeAPI, licenseAPI } from '@/api/index';
import ICountUp from 'vue-countup-v2';
import Swiper, { Navigation, Pagination, Autoplay, EffectFade } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

Swiper.use([Navigation, Pagination, Autoplay, EffectFade]);

export default {
  components: {
    ICountUp
  },
  data() {
    return {
      swiper: null,
      options: {
        useEasing: true,
        useGrouping: true,
        separator: ',',
        decimal: '.',
        prefix: '',
        suffix: ''
      },
      delay: 1000,
      isOpenMarketLoding: false,
      openMarketItemModel: [],
      openMarketItemTotalCount: {
        monthday: 0,
        today: 0,
        total: 0,
        yesterday: 0
      },
      isBusinessLoding: false,
      businessItemModel: [],
      businessItemTotalCount: {
        monthday: 0,
        today: 0,
        total: 0,
        yesterday: 0
      },
      isOfficeLoding: false,
      officeItemModel: [],
      isCorpLoding: false,
      corpItemModel: [],
      isLicenseLoding: false,
      licenseItemModel: [],
      corpTotalCount: {
        today: 0,
        yesterday: 0,
        monthday: 0,
        total: 0
      },
      officeTotalCount: {
        today: 0,
        yesterday: 0,
        monthday: 0,
        total: 0
      },
      licenseTotalCount: {
        today: 0,
        yesterday: 0,
        monthday: 0,
        total: 0
      },
      directDepthCategories: [
        '인테리어', '패션', '미용실', '카페', '숙박업',
        '네일아트', '음식점', '여행사', '학원', '청소',
        '반려동물', '헬스장', '노래방', '중개업', '운동',
        '생활/편의', '병원', '미디어', '물류/창고'
      ],
      activeIndex: 0,
      isCollecting: true,
      progressWidth: 0,
      lastUpdateTime: '',
      todayCollected: 0,
      collectionInterval: null
    }
  },
  async created() {
    this.isOpenMarketLoding = true
    this.isBusinessLoding = true
    await this.GetBusiness()
    await this.GetBusinessTotalCount()
    this.isBusinessLoding = false
    await this.GetOpenMarket()
    await this.GetOpenMarketTotalCount()
    this.isOpenMarketLoding = false

    // 새로운 DB 총 개수 가져오기
    await this.GetNewDBTotalCounts()

    // 새로운 DB 아이템 가져오기
    this.isOfficeLoding = true
    this.isCorpLoding = true
    this.isLicenseLoding = true

    await this.GetOffice()
    this.isOfficeLoding = false

    await this.GetCorp()
    this.isCorpLoding = false

    await this.GetLicense()
    this.isLicenseLoding = false

    // 실시간 수집 애니메이션 시작
    this.startCollectionAnimation()
    this.updateLastTime()

  },
  mounted() {
    // Swiper 초기화
    this.$nextTick(() => {
      this.swiper = new Swiper('.banner-swiper', {
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        loop: true,
        autoplay: {
          delay: 3500,
          disableOnInteraction: false
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        },
        speed: 1000,
        on: {
          slideChange: () => {
            if (this.swiper) {
              this.activeIndex = this.swiper.realIndex
            }
          }
        }
      })
    })
  },
  computed: {
    totalDBCount() {
      return this.businessItemTotalCount.total +
             this.openMarketItemTotalCount.total +
             this.corpTotalCount.total +
             this.officeTotalCount.total +
             this.licenseTotalCount.total
    },
    todayTotalCount() {
      return this.businessItemTotalCount.today +
             this.openMarketItemTotalCount.today +
             this.corpTotalCount.today +
             this.officeTotalCount.today +
             this.licenseTotalCount.today
    }
  },
  beforeDestroy() {
    if (this.collectionInterval) {
      clearInterval(this.collectionInterval)
    }
    if (this.swiper) {
      this.swiper.destroy()
    }
  },
  methods: {
    goPrev() {
      if (this.swiper) {
        this.swiper.slidePrev()
      }
    },
    goNext() {
      if (this.swiper) {
        this.swiper.slideNext()
      }
    },
    onReady: function(instance, CountUp) {
      instance.update(this.totalDBCount + 100);
    },
    async GetOpenMarket() {
      const itemResponse = await oepnMarketAPI.GetOpenMarket(1)
      if (itemResponse.success == true) {
        this.openMarketItemModel = itemResponse.data
      }
    },
    async GetOpenMarketTotalCount() {
      const itemResponse = await oepnMarketAPI.GetOpenMarketTotalCount()
      if (itemResponse.success == true) {
        this.openMarketItemTotalCount = itemResponse.data
      }
    },
    async GetBusiness() {
      const itemResponse = await businessAPI.GetBusiness(1, 10)
      if (itemResponse.success == true) {
        this.businessItemModel = itemResponse.data
      }
    },
    async GetBusinessTotalCount() {
      const itemResponse = await businessAPI.GetBusinessTotalCount()
      if (itemResponse.success == true) {
        this.businessItemTotalCount = itemResponse.data
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$bvToast.toast('복사되었습니다', {
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        })
      } catch (error) {
        console.error('복사 실패:', error)
      }
    },
    GetLink(type, link) {
      if(link == null || link == ""){
        alert('로그인 후 사용 부탁드립니다.')
        return
      }
      else if(type == "오픈마켓" && link.indexOf('/') == -1){
        window.open(`https://smartstore.naver.com/${link}`);
      }
      else if (type == "오픈마켓"){
        window.open(link);
      }
      else if(type == "사업자"){
        window.open(`https://map.naver.com/p/entry/place/${link}`);
      }
    },
    isNewItem(createDate) {
      const today = this.$dayjs()
      const itemDate = this.$dayjs(createDate)
      return today.diff(itemDate, 'day') === 0
    },
    getMarketClass(market) {
      const classMap = {
        'N사': 'badge-naver',
        'A사': 'badge-auction',
        'G사': 'badge-gmarket',
        'C사': 'badge-coupang'
      }
      return classMap[market] || 'badge-default'
    },
    goToCategorySearch(category) {
      this.$router.push(`/category-search/${category}`)
    },
    getCategoryIcon(category) {
      const iconMap = {
        '인테리어': 'fa fa-paint-brush',
        '패션': 'fa fa-shopping-bag',
        '미용실': 'fa fa-scissors',
        '카페': 'fa fa-coffee',
        '숙박업': 'fa fa-bed',
        '네일아트': 'fa fa-hand-o-right',
        '음식점': 'fa fa-cutlery',
        '여행사': 'fa fa-plane',
        '학원': 'fa fa-graduation-cap',
        '청소': 'fa fa-home',
        '반려동물': 'fa fa-paw',
        '헬스장': 'fa fa-heartbeat',
        '노래방': 'fa fa-microphone',
        '중개업': 'fa fa-handshake-o',
        '운동': 'fa fa-bicycle',
        '생활/편의': 'fa fa-shopping-cart',
        '병원': 'fa fa-hospital-o',
        '미디어': 'fa fa-video-camera',
        '물류/창고': 'fa fa-truck'
      }
      return iconMap[category] || 'fa fa-folder'
    },
    startCollectionAnimation() {
      // 진행바 애니메이션
      this.collectionInterval = setInterval(() => {
        if (this.progressWidth >= 100) {
          this.progressWidth = 0
          this.isCollecting = false
          // 수집 완료 후 잠시 대기
          setTimeout(() => {
            this.isCollecting = true
            this.simulateDataCollection()
          }, 2000)
        } else {
          this.progressWidth += Math.random() * 3 + 1
        }
      }, 200)

      // 초기 데이터 설정
      this.todayCollected = this.businessItemTotalCount.today + this.openMarketItemTotalCount.today

      // 주기적으로 수집 데이터 업데이트 시뮬레이션
      setInterval(() => {
        this.simulateDataCollection()
      }, 5000)
    },
    simulateDataCollection() {
      // 랜덤하게 수집 데이터 업데이트
      const randomIncrease = Math.floor(Math.random() * 10) + 1
      this.todayCollected += randomIncrease
      this.updateLastTime()
    },
    updateLastTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      this.lastUpdateTime = `${year}-${month}-${day} ${hours}:${minutes}`
    },
    async GetOffice() {
      try {
        const itemResponse = await officeAPI.GetOffice(1)
        if (itemResponse.success == true) {
          this.officeItemModel = itemResponse.data
        }
      } catch (error) {
        console.error('Error fetching office data:', error)
      }
    },
    async GetCorp() {
      try {
        const itemResponse = await corpAPI.GetCorp(1)
        if (itemResponse.success == true) {
          this.corpItemModel = itemResponse.data
        }
      } catch (error) {
        console.error('Error fetching corp data:', error)
      }
    },
    async GetLicense() {
      try {
        const itemResponse = await licenseAPI.GetLicense(1)
        if (itemResponse.success == true) {
          this.licenseItemModel = itemResponse.data
        }
      } catch (error) {
        console.error('Error fetching license data:', error)
      }
    },
    async GetNewDBTotalCounts() {
      try {
        // Corp total count
        const corpResponse = await corpAPI.GetCorpTotalCount()
        if (corpResponse.success == true) {
          this.corpTotalCount = corpResponse.data
        }

        // Office total count
        const officeResponse = await officeAPI.GetOfficeTotalCount()
        if (officeResponse.success == true) {
          this.officeTotalCount = officeResponse.data
        }

        // License total count
        const licenseResponse = await licenseAPI.GetLicenseTotalCount()
        if (licenseResponse.success == true) {
          this.licenseTotalCount = licenseResponse.data
        }
      } catch (error) {
        console.error('Error fetching DB counts:', error)
      }
    }
  }
}
</script>

<style scoped>
.main-page {
  padding-top: 0;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

/* 배너 섹션 */
.hero-banner {
  padding: 20px 0 20px 0;
  margin-bottom: 40px;
  transition: background 1s ease-in-out;
  position: relative;
}

.banner-wrapper {
  position: relative;
}

.banner-container {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  padding: 0 40px;
}

.banner-swiper {
  width: 100%;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
}

.banner-slide {
  width: 100%;
  height: 100%;
  display: block;
  position: relative;
  border-radius: 20px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.banner-slide-1 {
  background-image: url('~@/assets/images/banner/1.png');
}

.banner-slide-2 {
  background-image: url('~@/assets/images/banner/2.png');
}

.banner-slide-3 {
  background-image: url('~@/assets/images/banner/3.png');
}

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(30,144,255,0.7) 0%, rgba(65,105,225,0.7) 100%);
  z-index: 1;
}

.banner-content {
  flex: 1;
  color: white;
  z-index: 2;
  animation: slideInLeft 0.8s ease;
}

.banner-badge {
  display: inline-block;
  background: rgba(255,255,255,0.3);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 16px;
  backdrop-filter: blur(10px);
}

.banner-title {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
  line-height: 1.2;
}

.banner-desc {
  font-size: 20px;
  margin-bottom: 30px;
  opacity: 0.95;
  line-height: 1.5;
}

.btn-banner {
  background: rgba(255,255,255,0.95);
  color: #1e90ff;
  border: none;
  padding: 14px 36px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  backdrop-filter: blur(10px);
}

.btn-banner:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 30px rgba(0,0,0,0.3);
  background: white;
}

.btn-banner i {
  transition: transform 0.3s;
}

.btn-banner:hover i {
  transform: translateX(5px);
}

.banner-image {
  position: absolute;
  right: 80px;
  top: 50%;
  transform: translateY(-50%);
}

.floating-icon {
  position: relative;
  animation: float 3s ease-in-out infinite;
}

.floating-icon i {
  font-size: 180px;
  color: rgba(255,255,255,0.2);
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.1));
}

/* 커스텀 인디케이터 */
.custom-indicators {
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 20px;
  z-index: 10;
}

.indicator-item {
  cursor: pointer;
  padding: 8px 16px;
  background: white;
  border-radius: 50px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
}

.indicator-item:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.indicator-item.active {
  background: linear-gradient(135deg, #1e90ff 0%, #4169e1 100%);
  color: white;
}

.indicator-number {
  font-size: 14px;
  font-weight: 600;
  position: relative;
  z-index: 2;
}

.indicator-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: #1e90ff;
  width: 0;
  transition: width 5s linear;
}

.indicator-item.active .indicator-progress {
  width: 100%;
}

/* 모바일 네비게이션 컨트롤 */
.mobile-nav-control {
  display: none;
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
  align-items: center;
  gap: 8px;
}

.mobile-nav-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(50, 50, 50, 0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 5px;
  font-size: 12px;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.mobile-nav-btn:hover {
  background: rgba(50, 50, 50, 0.85);
  transform: scale(1.05);
}

.mobile-nav-btn:active {
  transform: scale(0.95);
  background: rgba(30, 30, 30, 0.9);
}

.mobile-nav-btn i {
  font-size: 12px;
  font-weight: bold;
}

.slide-counter {
  font-size: 11px;
  font-weight: 700;
  color: white;
  background: rgba(50, 50, 50, 0.7);
  padding: 5px 10px;
  border-radius: 5px;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  min-width: 36px;
  text-align: center;
}

/* Swiper 네비게이션 커스터마이징 */
::v-deep .swiper-button-prev,
::v-deep .swiper-button-next {
  width: 50px;
  height: 50px;
  background: rgba(255,255,255,0.9);
  border-radius: 50%;
  opacity: 0.8;
  transition: all 0.3s;
  color: #1e90ff;
}

::v-deep .swiper-button-prev::after,
::v-deep .swiper-button-next::after {
  font-size: 20px;
  font-weight: bold;
}

::v-deep .swiper-button-prev {
  left: 20px;
}

::v-deep .swiper-button-next {
  right: 20px;
}

::v-deep .swiper-button-prev:hover,
::v-deep .swiper-button-next:hover {
  opacity: 1;
  transform: scale(1.1);
  background: white;
}

/* Swiper 페이지네이션 커스터마이징 */
::v-deep .swiper-pagination {
  bottom: 20px;
}

::v-deep .swiper-pagination-bullet {
  width: 12px;
  height: 12px;
  background: rgba(255,255,255,0.5);
  opacity: 1;
  transition: all 0.3s;
}

::v-deep .swiper-pagination-bullet-active {
  width: 30px;
  border-radius: 6px;
  background: white;
}

::v-deep .swiper-slide {
  text-align: center;
  font-size: 18px;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

::v-deep .swiper-container {
  width: 100%;
  height: 100%;
}

::v-deep .swiper-wrapper {
  display: flex;
  align-items: center;
}

/* 애니메이션 */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* 통계 섹션 */
.stats-section {
  background: #f8f9fa;
  padding: 40px 0;
  margin-bottom: 60px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  transition: all 0.3s;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 20px rgba(0,0,0,0.12);
}

.stat-card.highlight {
  background: #1e90ff;
  color: white;
}

.stat-card.total-card {
  background: #1e90ff;
  color: white;
  position: relative;
}

.stat-badge.total-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255,255,255,0.2);
  color: white;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.stat-icon {
  width: 60px;
  height: 60px;
  background: #e7f3ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #1e90ff;
}

.stat-card.highlight .stat-icon,
.stat-card.total-card .stat-icon {
  background: rgba(255,255,255,0.2);
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-content label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 700;
  letter-spacing: -0.3px;
}

.stat-card.today-card .stat-content label {
  color: #1e90ff;
  font-weight: 800;
  font-size: 15px;
}

.stat-card.total-card .stat-content label {
  color: rgba(255,255,255,0.95);
  font-size: 15px;
  font-weight: 800;
}

.stat-content h3 {
  font-size: 32px;
  font-weight: 900;
  color: #2c3e50;
  margin: 0;
  letter-spacing: -1px;
  display: flex;
  align-items: baseline;
}

.stat-card.today-card .stat-content h3 {
  color: #1e90ff;
  font-size: 36px;
  font-weight: 900;
}

.stat-card.yesterday-card .stat-content h3 {
  font-size: 30px;
  font-weight: 800;
  color: #495057;
}

.stat-card.month-card .stat-content h3 {
  font-size: 30px;
  font-weight: 800;
  color: #495057;
}

.stat-card.total-card .stat-content h3 {
  color: white;
  font-size: 38px;
  font-weight: 900;
}

.stat-content h3 span {
  font-size: 18px;
  font-weight: 700;
  margin-left: 6px;
  color: #666;
}

.stat-card.today-card h3 span {
  color: #4169e1;
  font-size: 19px;
}

.stat-card.total-card h3 span {
  color: rgba(255,255,255,0.9);
  font-size: 20px;
}

/* 카테고리 섹션 */
.category-section {
  padding: 60px 0;
  background: white;
}

.section-header {
  text-align: center;
  margin-bottom: 50px;
}

.section-header h2 {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
}

.section-header p {
  font-size: 16px;
  color: #666;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 20px;
}

.category-item {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.category-item:hover {
  border-color: #1e90ff;
  background: #f0f8ff;
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(30,144,255,0.2);
}

.category-icon {
  width: 50px;
  height: 50px;
  background: #f5f5f5;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  font-size: 22px;
  color: #333;
  transition: all 0.3s;
}

.category-item:hover .category-icon {
  background: #1e90ff;
  color: white;
}

.category-item span {
  display: block;
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* 실시간 수집 아이콘 */
.collecting-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
  padding: 4px 12px;
  background: #f0f8ff;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #1e90ff;
  vertical-align: middle;
}

.collecting-dot {
  font-size: 8px;
  color: #28a745;
  animation: blink 1.5s infinite;
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
    color: #28a745;
  }
  50% {
    opacity: 0.3;
    color: #20c997;
  }
}

.collecting-text {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: #1e90ff;
}

@keyframes shimmer {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
}

/* DB 섹션 */
.db-section {
  padding: 60px 0;
  background: white;
}

.db-section.bg-light {
  background: #f8f9fa;
}

.db-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
}

.db-title {
  display: flex;
  align-items: center;
  gap: 20px;
}

.db-icon {
  width: 50px;
  height: 50px;
  background: #1e90ff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.db-title h3 {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.db-title p {
  font-size: 14px;
  color: #666;
  margin: 5px 0 0;
}

.db-stats {
  display: flex;
  gap: 30px;
}

.db-stat {
  text-align: right;
}

.db-stat .label {
  display: block;
  font-size: 12px;
  color: #999;
  margin-bottom: 5px;
}

.db-stat .value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #1e90ff;
}

/* 모바일 스크롤 안내 배지 */
.mobile-scroll-notice {
  display: none;
  background: #6c757d;
  color: white;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 12px;
  text-align: center;
}

/* 테이블 스타일 */
.db-table-wrapper {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
}


.db-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.db-table thead {
  background: #f8f9fa;
}

.db-table th {
  padding: 15px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  border-right: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}

.db-table th:last-child {
  border-right: none;
}

.db-table tbody tr {
  transition: all 0.2s;
}

.db-table tbody tr:hover {
  background: #f8f9fa;
}

.db-table tbody tr:last-child td {
  border-bottom: none;
}

.db-table td {
  padding: 15px;
  font-size: 14px;
  color: #333;
  border-right: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}

.db-table td:last-child {
  border-right: none;
}

.db-table td:first-child {
  font-weight: 600;
}

.company-name {
  font-weight: 500;
  color: #333;
  position: relative;
}

.company-name span {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  padding: 4px 8px;
  border-radius: 6px;
}

.company-name span:hover {
  color: #1e90ff;
  background: #e7f3ff;
  transform: translateX(5px);
}

.new-icon {
  width: 20px !important;
  height: auto;
  animation: blink 1s infinite;
  vertical-align: middle;
  margin-left: 5px;
  filter: drop-shadow(0 2px 4px rgba(255,0,0,0.3));
}

@keyframes blink {
  0%, 50%, 100% {
    opacity: 1;
  }
  25%, 75% {
    opacity: 0.5;
  }
}

.phone {
  color: #666;
  white-space: nowrap;
}

.phone i {
  color: #1e90ff;
  margin-right: 8px;
  font-size: 12px;
  background: #e7f3ff;
  padding: 5px;
  border-radius: 50%;
}

.address {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 업종 컬럼 width 제한 */
.db-table td:nth-child(2) {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 마켓 배지 */
.badge-naver {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background: linear-gradient(135deg, #03c75a 0%, #00a046 100%);
  color: white;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(3,199,90,0.3);
  transition: all 0.3s;
}

.badge-naver:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(3,199,90,0.4);
}

.badge-coupang {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background: linear-gradient(135deg, #d4145a 0%, #b01048 100%);
  color: white;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(212,20,90,0.3);
  transition: all 0.3s;
}

.badge-coupang:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(212,20,90,0.4);
}

.badge-gmarket {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background: linear-gradient(135deg, #00d082 0%, #00b06e 100%);
  color: white;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0,208,130,0.3);
  transition: all 0.3s;
}

.badge-gmarket:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0,208,130,0.4);
}

.badge-auction {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background: linear-gradient(135deg, #ff6b35 0%, #ff5722 100%);
  color: white;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(255,107,53,0.3);
  transition: all 0.3s;
}

.badge-auction:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255,107,53,0.4);
}

.badge-default {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  color: white;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(108,117,125,0.3);
  transition: all 0.3s;
}

.badge-default:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(108,117,125,0.4);
}

/* 상세 버튼 */
.btn-detail {
  background: #1e90ff;
  color: white;
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  position: relative;
  overflow: hidden;
}

.btn-detail::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255,255,255,0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn-detail:hover {
  transform: scale(1.05);
  background: #0078d4;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.btn-detail:hover::before {
  width: 50px;
  height: 50px;
}

.action {
  text-align: center;
}

/* 로딩 */
.loading-wrapper {
  padding: 20px;
  background: white;
  border-radius: 12px;
}

/* 실시간 수집 상태 바 */
.collection-status-bar {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px 0;
  margin-bottom: 30px;
  border-bottom: 2px solid #dee2e6;
}

.status-bar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: white;
  border-radius: 50px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.pulse-dot {
  width: 12px;
  height: 12px;
  background: #dc3545;
  border-radius: 50%;
  position: relative;
}

.status-indicator.active .pulse-dot {
  background: #28a745;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
}

.status-text {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.collection-info {
  display: flex;
  gap: 30px;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
}

.info-item i {
  color: #1e90ff;
  font-size: 16px;
}

.info-item strong {
  color: #1e90ff;
  font-weight: 700;
}

.progress-bar-wrapper {
  flex: 1;
  min-width: 200px;
  max-width: 400px;
}

.progress-bar-bg {
  height: 6px;
  background: #dee2e6;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  height: 100%;
  background: #1e90ff;
  border-radius: 10px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-bar-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* 반응형 */
@media (max-width: 1200px) {
  .banner-container {
    padding: 0 60px;
  }

  ::v-deep .swiper-button-prev {
    left: 10px;
  }

  ::v-deep .swiper-button-next {
    right: 10px;
  }

  .banner-slide {
    padding: 0 40px;
  }

  .banner-content h1 {
    font-size: 36px;
  }

  .banner-content p {
    font-size: 18px;
  }
}

@media (max-width: 768px) {
  .main-page {
    padding-top: 0;
  }

  .hero-banner {
    padding: 15px 0;
    margin-bottom: 30px;
  }

  .banner-container {
    padding: 0 15px;
    position: relative;
  }

  /* 모바일 네비게이션 표시 */
  .mobile-nav-control {
    display: flex;
  }

  .banner-swiper {
    height: 300px;
    border-radius: 12px;
  }

  .banner-slide {
    padding: 0;
    height: 300px;
    background-size: cover;
    background-position: center;
  }

  .banner-slide-1 {
    background-image: url('~@/assets/images/mobile/1.png');
    background-size: cover;
  }

  .banner-slide-2 {
    background-image: url('~@/assets/images/mobile/2.png');
    background-size: cover;
  }

  .banner-slide-3 {
    background-image: url('~@/assets/images/mobile/3.png');
    background-size: cover;
  }

  /* 데스크톱 네비게이션 숨기기 */
  ::v-deep .swiper-button-prev,
  ::v-deep .swiper-button-next {
    display: none !important;
  }

  ::v-deep .swiper-pagination {
    bottom: 15px;
  }

  ::v-deep .swiper-pagination-bullet {
    width: 8px;
    height: 8px;
  }

  ::v-deep .swiper-pagination-bullet-active {
    width: 20px;
  }

  .banner-content h1 {
    font-size: 28px;
  }

  .banner-content p {
    font-size: 16px;
  }

  .banner-image {
    display: none;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .category-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 15px;
  }

  .db-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    padding-bottom: 15px;
  }

  .db-title {
    width: 100%;
  }

  .db-icon {
    width: 45px;
    height: 45px;
    font-size: 22px;
  }

  .db-title h3 {
    font-size: 20px;
  }

  .db-title p {
    font-size: 13px;
  }

  .db-stats {
    width: 100%;
    justify-content: space-around;
    gap: 15px;
    flex-wrap: wrap;
  }

  .db-stat {
    background: white;
    padding: 12px 16px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    flex: 1;
    min-width: 140px;
    text-align: center;
  }

  .db-stat .label {
    font-size: 11px;
    margin-bottom: 6px;
    color: #666;
  }

  .db-stat .value {
    font-size: 18px;
  }

  .db-table-wrapper {
    overflow-x: auto;
  }

  .db-table {
    min-width: 700px;
  }

  .db-table th {
    font-size: 12px;
    padding: 10px;
  }

  .db-table td {
    font-size: 12px;
    padding: 10px;
  }

  /* 모바일 스크롤 안내 배지 표시 */
  .mobile-scroll-notice {
    display: flex;
  }

  /* collection-status-bar 모바일 최적화 */
  .collection-status-bar {
    padding: 15px 0;
    margin-bottom: 20px;
  }

  .status-bar-content {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .status-indicator {
    justify-content: center;
    padding: 10px 16px;
  }

  .collection-info {
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .info-item {
    font-size: 13px;
    justify-content: flex-start;
    background: white;
    padding: 12px 16px 12px 20px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    transition: all 0.3s;
    width: 100%;
  }

  .info-item:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }

  .info-item i {
    font-size: 16px;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #e7f3ff 0%, #d0e9ff 100%);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .status-indicator .pulse-dot {
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.3);
      opacity: 0.7;
    }
  }

  .status-indicator i {
    animation: rotate 3s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .progress-bar-wrapper {
    max-width: 100%;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .hero-banner {
    padding: 10px 0;
    margin-bottom: 20px;
  }

  .banner-container {
    padding: 0 10px;
  }

  .mobile-nav-control {
    top: 10px;
    right: 10px;
    gap: 6px;
  }

  .mobile-nav-btn {
    width: 26px;
    height: 26px;
  }

  .mobile-nav-btn i {
    font-size: 11px;
  }

  .slide-counter {
    font-size: 10px;
    padding: 4px 8px;
    min-width: 34px;
  }

  .banner-swiper {
    height: 250px;
    border-radius: 10px;
  }

  .banner-slide {
    height: 250px;
  }

  ::v-deep .swiper-pagination {
    bottom: 10px;
  }

  ::v-deep .swiper-pagination-bullet {
    width: 6px;
    height: 6px;
  }

  ::v-deep .swiper-pagination-bullet-active {
    width: 16px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .category-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  .category-item {
    padding: 15px 5px;
  }

  .category-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .category-item span {
    font-size: 12px;
  }

  /* collection-status-bar 작은 모바일 최적화 */
  .collection-status-bar {
    padding: 12px 0;
  }

  .status-indicator {
    padding: 8px 12px;
  }

  .status-text {
    font-size: 13px;
  }

  .pulse-dot {
    width: 10px;
    height: 10px;
  }

  .info-item {
    font-size: 12px;
  }

  .info-item i {
    font-size: 13px;
  }

  .info-item span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .progress-bar-wrapper {
    min-width: 100%;
  }

  .progress-bar-bg {
    height: 5px;
  }

  /* db-stats 작은 모바일 최적화 */
  .db-header {
    gap: 12px;
    padding-bottom: 12px;
  }

  .db-icon {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }

  .db-title h3 {
    font-size: 18px;
  }

  .db-title p {
    font-size: 12px;
  }

  .db-stats {
    gap: 10px;
  }

  .db-stat {
    padding: 10px 12px;
    min-width: 130px;
  }

  .db-stat .label {
    font-size: 10px;
    margin-bottom: 5px;
  }

  .db-stat .value {
    font-size: 16px;
  }

  .collecting-indicator {
    font-size: 10px;
    padding: 3px 8px;
    margin-left: 8px;
  }

  .collecting-dot {
    font-size: 7px;
  }

  .collecting-text {
    font-size: 9px;
  }

  /* 모바일 스크롤 안내 배지 작은 화면 */
  .mobile-scroll-notice {
    padding: 8px 12px;
    font-size: 12px;
    margin-bottom: 10px;
  }
}
</style>